import { fork } from '@cortex/utils/fork';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import '../../../components/refinement/refinement.css';
import { PageHeader } from '../../../components/common/PageHeader';
import { ChatView } from '../../../components/refinement/ChatView';
import { RefinementSidebar } from '../../../components/refinement/RefinementSidebar';
import { ThreadModal } from '../../../components/refinement/ThreadModal';
import { routes } from '../../../lib/constants';
import {
  useTheopolis,
  type IAgent,
  type IMessage,
  type ISchedule,
  type IThread,
} from '../../../lib/theopolis';

export interface IRefinementSearch {
  thread?: string;
}

export const Route = createFileRoute('/refinement/history/')({
  component: RefinementPage,
  validateSearch: (raw: Record<string, unknown>): IRefinementSearch => {
    const t = raw.thread;
    return typeof t === 'string' && t.length > 0 ? { thread: t } : {};
  },
});

function RefinementPage() {
  const theo = useTheopolis();
  const nav = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  // ── Agents ────────────────────────────────────────────────────────────
  // The console no longer surfaces agent CRUD. /refinement specifically
  // needs the agent classified `type === 'refinement'` (sandbox-eligible,
  // long-running). The header-bar chat dialog uses a separate agent
  // (`type === 'chat'`) — see ChatLauncher.
  const agentsQuery = useQuery({
    queryKey: ['theopolis', 'agents'],
    queryFn: () => theo.get('agents').json<IAgent[]>(),
  });
  const agents = useMemo<IAgent[]>(() => {
    const refinementAgent = agentsQuery.data?.find((a) => a.type === 'refinement');
    return refinementAgent ? [refinementAgent] : [];
  }, [agentsQuery.data]);

  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  // Default to the first agent once the list arrives.
  useEffect(() => {
    if (activeAgentId) return;
    const first = agents[0];
    if (first) setActiveAgentId(first.id);
  }, [agents, activeAgentId]);

  // ── Threads ───────────────────────────────────────────────────────────
  const threadsQuery = useQuery({
    queryKey: ['theopolis', 'threads'],
    queryFn: () => theo.get('threads').json<IThread[]>(),
    refetchInterval: 30_000,
  });
  const threads = threadsQuery.data ?? [];

  // Schedules — used by the sidebar to resolve `tags.scheduledBy` IDs into
  // human-readable names on threads produced by a cron fire. Cheap query;
  // no need to gate it on whether any scheduled threads exist.
  const schedulesQuery = useQuery({
    queryKey: ['theopolis', 'schedules'],
    queryFn: () => theo.get('schedules').json<ISchedule[]>(),
    refetchInterval: 60_000,
  });
  const schedules = schedulesQuery.data ?? [];

  const activeThreadId = search.thread ?? null;
  const activeThread = threads.find((t) => t.id === activeThreadId) ?? null;
  const activeAgent = agents.find((a) => a.id === (activeThread?.agentId ?? activeAgentId)) ?? null;

  function selectThread(id: string) {
    fork(nav({ search: { thread: id } }));
  }

  // ── Messages for the active thread ────────────────────────────────────
  const messagesQuery = useQuery({
    queryKey: ['theopolis', 'messages', activeThreadId],
    queryFn: () => {
      // `enabled` below guarantees activeThreadId is non-null when this fires.
      const id = activeThreadId ?? '';
      return theo.get(`threads/${encodeURIComponent(id)}/messages`).json<IMessage[]>();
    },
    enabled: Boolean(activeThreadId),
  });

  // ── Modals ────────────────────────────────────────────────────────────
  const [threadModalOpen, setThreadModalOpen] = useState(false);

  return (
    <>
      <PageHeader
        title="History"
        actions={
          <Link to={routes.REFINEMENT_SCHEDULES} className="btn btn--outline btn--sm">
            Schedules
          </Link>
        }
      />

      {agentsQuery.error ? (
        <div className="alert alert--danger" style={{ marginBottom: 'var(--sp-3)' }}>
          Failed to load agents: {(agentsQuery.error as Error).message}
        </div>
      ) : null}

      <div className="refinement-shell">
        <RefinementSidebar
          threads={threads}
          isThreadsLoading={threadsQuery.isLoading}
          schedules={schedules}
          activeAgentId={activeAgentId}
          activeThreadId={activeThreadId}
          onThreadSelect={selectThread}
          onNewThread={() => {
            if (!activeAgentId) return;
            setThreadModalOpen(true);
          }}
        />

        {activeThread ? (
          <ChatView
            key={activeThread.id}
            thread={activeThread}
            agent={activeAgent}
            initialMessages={messagesQuery.data ?? []}
            isMessagesLoading={messagesQuery.isLoading}
            refetchThread={() => {
              fork(threadsQuery.refetch());
              fork(messagesQuery.refetch());
            }}
          />
        ) : (
          <section className="refinement-main">
            <div className="refinement-empty">
              <div className="fk-empty-state">
                <div className="fk-empty-state__message">
                  <div className="fk-empty-state__message-title">No session selected</div>
                  <div className="fk-empty-state__message-desc">
                    Open a past refinement session from the list, or start a new one to begin
                    experimenting with Theopolis.
                  </div>
                  <div className="fk-empty-state__cta">
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => setThreadModalOpen(true)}
                      disabled={!activeAgentId}
                      title={activeAgentId ? 'New session' : 'No agent configured'}
                    >
                      New session
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>

      <ThreadModal
        open={threadModalOpen}
        onClose={() => setThreadModalOpen(false)}
        agentId={activeAgentId}
        onCreated={(t) => {
          setThreadModalOpen(false);
          selectThread(t.id);
        }}
      />
    </>
  );
}
